// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  FulfillmentOrderLineItemStatus,
  fulfillmentOrderLineItemStatusSchema,
} from '@/constants/fulfillment-order-line-item-status-enum';
import type { ChipProps } from '@mui/material';

// =============================================================================
// Types
// =============================================================================

export type FulfillmentOrderPhase = 'draft' | 'open' | 'shipped' | 'delivered';
export type FulfillmentOrderSubstatus = 'partial' | 'full' | 'overage';

// =============================================================================
// Chip + phase config
// =============================================================================

type ChipColor = ChipProps['color'];
type ChipVariant = ChipProps['variant'] | 'text';

interface ChipConfig {
  color: ChipColor;
  variant: ChipVariant;
}

interface PhaseConfig {
  label: string;
  chipConfig: ChipConfig;
}

export const PHASE_CONFIG: Record<FulfillmentOrderPhase, PhaseConfig> = {
  draft: {
    label: 'Draft',
    chipConfig: { color: 'default', variant: 'text' },
  },
  open: {
    label: 'Open',
    chipConfig: { color: 'default', variant: 'filled' },
  },
  shipped: {
    label: 'Shipped',
    chipConfig: { color: 'info', variant: 'filled' },
  },
  delivered: {
    label: 'Delivered',
    chipConfig: { color: 'primary', variant: 'filled' },
  },
};

// =============================================================================
// Status → phase/modifier mapping
// =============================================================================

const STATUS_TO_PHASE: Record<
  FulfillmentOrderLineItemStatus,
  FulfillmentOrderPhase
> = {
  DRAFT: 'draft',
  OPEN_FULL: 'open',
  OPEN_PARTIAL: 'open',
  OPEN_OVERAGE: 'open',
  SHIPPED_FULL: 'shipped',
  SHIPPED_PARTIAL: 'shipped',
  SHIPPED_OVERAGE: 'shipped',
  DELIVERED_FULL: 'delivered',
  DELIVERED_PARTIAL: 'delivered',
  DELIVERED_OVERAGE: 'delivered',
};

const PHASES_WITH_MODIFIERS: ReadonlySet<FulfillmentOrderPhase> =
  new Set<FulfillmentOrderPhase>(['open', 'shipped', 'delivered']);

const SUBSTATUS_VALUES = new Set(['partial', 'full', 'overage']);

interface ParsedStatus {
  phase: FulfillmentOrderPhase;
  modifier: FulfillmentOrderSubstatus | null;
}

export function parseStatus(
  status: FulfillmentOrderLineItemStatus
): ParsedStatus {
  const phase = STATUS_TO_PHASE[status];
  if (!PHASES_WITH_MODIFIERS.has(phase)) return { phase, modifier: null };
  const parts = status.split('_');
  const suffix = parts[parts.length - 1].toLowerCase();
  const modifier = SUBSTATUS_VALUES.has(suffix)
    ? (suffix as FulfillmentOrderSubstatus)
    : null;
  return { phase, modifier };
}

export function getModifierVariant(
  phase: FulfillmentOrderPhase,
  modifier: FulfillmentOrderSubstatus | null
): ChipVariant {
  if (modifier === 'partial') return 'outlined';
  return PHASE_CONFIG[phase].chipConfig.variant;
}

// =============================================================================
// Line item tooltips
// =============================================================================

export const LINE_ITEM_STATUS_TOOLTIPS: Record<
  FulfillmentOrderLineItemStatus,
  string
> = {
  DRAFT: 'No shipments have been created for this line item',
  OPEN_FULL: 'This line item is fully open for shipment',
  OPEN_PARTIAL: 'Part of this line item is open for shipment',
  OPEN_OVERAGE: 'More than the requested quantity is open for shipment',
  SHIPPED_FULL: 'This line item has been fully shipped',
  SHIPPED_PARTIAL: 'Part of this line item has been shipped',
  SHIPPED_OVERAGE: 'More than the requested quantity has been shipped',
  DELIVERED_FULL: 'This line item has been fully delivered',
  DELIVERED_PARTIAL: 'Part of this line item has been delivered',
  DELIVERED_OVERAGE: 'More than the requested quantity has been delivered',
};

// Ensure LINE_ITEM_STATUS_TOOLTIPS covers all schema values at compile time
const _exhaustiveTooltipCheck: Record<
  (typeof fulfillmentOrderLineItemStatusSchema.options)[number],
  string
> = LINE_ITEM_STATUS_TOOLTIPS;
void _exhaustiveTooltipCheck;
